import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { Avatar, Box, Button, Card, CardContent, Chip, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

function getInitials(playerName) {
  return playerName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function StartScreen({ playerName, onNavigate, onSavePlayerName, onPreloadFirstGameMedia }) {
  const [draftPlayerName, setDraftPlayerName] = useState(playerName);
  const [hasSaved, setHasSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const hasNameChanges = draftPlayerName.trim() !== playerName;

  useEffect(() => {
    setDraftPlayerName(playerName);
  }, [playerName]);

  useEffect(() => {
    onPreloadFirstGameMedia?.();
  }, [onPreloadFirstGameMedia]);

  const handleNameChange = (event) => {
    setDraftPlayerName(event.target.value);
    setHasSaved(false);
    setSaveError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSaveError('');

    try {
      await onSavePlayerName(draftPlayerName);
      setHasSaved(true);
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="screen start-screen">
      <Stack spacing={3} sx={{ width: '100%' }}>
        <Stack spacing={1.5}>
          <Chip label="No account needed" color="primary" sx={{ alignSelf: 'flex-start' }} />
          <Typography variant="h1">Fake or Frame</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 340 }}>
            Watch each clip and decide whether it is AI-generated or a real frame. Build your streak, sharpen your eye,
            and see how your guesses stack up on the leaderboard.
          </Typography>
        </Stack>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Display name
                </Typography>
                <Typography fontWeight={900}>Choose how you appear in the game</Typography>
              </Box>

              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', color: 'background.default', fontWeight: 900 }}>
                  {getInitials(playerName)}
                </Avatar>
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BadgeRoundedIcon color="primary" fontSize="small" />
                    <Typography fontWeight={900}>{playerName}</Typography>
                  </Stack>
                  <Typography color="text.secondary">
                    This name is saved locally in this browser so you can keep playing without an account.
                  </Typography>
                </Stack>
              </Stack>

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={1.5}>
                  <TextField
                    value={draftPlayerName}
                    onChange={handleNameChange}
                    label="Player name"
                    inputProps={{ maxLength: 32 }}
                    fullWidth
                  />
                  <Button
                    type="submit"
                    variant="outlined"
                    startIcon={hasSaved && !hasNameChanges ? <CheckRoundedIcon /> : <SaveRoundedIcon />}
                    disabled={!hasNameChanges || saving}
                  >
                    {saving ? 'Saving' : hasSaved && !hasNameChanges ? 'Saved' : 'Save Display Name'}
                  </Button>
                  {saveError && <Typography color="error">{saveError}</Typography>}
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Button
          variant="contained"
          size="large"
          startIcon={<PlayArrowRoundedIcon />}
          onClick={() => onNavigate('/game')}
        >
          Start Playing
        </Button>
      </Stack>
    </Box>
  );
}

export default StartScreen;
